<template>
    <div class="chat-wrapper" :class="{ 'chat-wrapper--inline': inline }">
        <h3 class="chat-title">{{ activeSession?.name || 'Без названия' }}</h3>

        <div class="chat-scroll" ref="chatContentRef">
            <div class="chat-container">
                <div class="chat-content">
                    <div ref="topSentinelRef" class="top-sentinel"></div>
                    <EmptyChat v-if="messages.length === 0 && !isStreaming && !isLoading" @select="quickSend" />
                    <template v-for="(msg, index) in messages" :key="index">
                        <template v-if="!isHiddenToolMessage(msg)">
                            <FilesMessage v-if="isFilesMessage(msg)" :msg="msg" />

                            <ToolResultMessage v-else-if="isToolMessage(msg)" :msg="msg" />

                            <ActionMessage v-else-if="isActionMessage(msg)" :msg="msg" :status="getActionStatus(index)"
                                :showConfirmation="msg.typeChunk === 'CONFIRMATION_REQUIRED' && isConfirmation"
                                @confirm="confirmAction" @reject="rejectAction" />

                            <AnswerMessage v-else-if="isAnswerMessage(msg)" :message_id="msg.id"
                                :content="msg.content.message" :date="msg.created_at || msg.content.created_at" />

                            <ButtonsMessage v-else-if="isButtonsMessage(msg)" :msg="msg"
                                :disabled="isStreaming || isButtonSelected(index)" :selectedLabel="getSelectedLabel(index)"
                                @select="(actionId, value, label) => handleButtonClick(actionId, value, label, index)" />

                            <UserMessage v-else-if="isUserMessage(msg)" :content="msg.content" />
                        </template>
                    </template>

                    <LoadingIndicator v-if="isStreaming" />
                    <ErrorNotification v-if="error" :message="error" />
                </div>
            </div>
        </div>

        <div class="chat-input-wrapper">
            <div class="chat-input-area">
                <div class="chat-input">
                    <textarea ref="textareaRef" v-model="messageText" placeholder="Задайте вопрос..." rows="1"
                        @input="adjustHeight" @keydown="handleKeydown" />
                    <div class="input-button">
                        <span>Файл</span>
                        <image-button :src="ArrowRightIcon" blue hint="Отправить"
                            :disabled="isStreaming || !messageText.trim()" @click="sendMessage" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch, defineProps, defineEmits } from 'vue';
import { storeToRefs } from 'pinia';
import { useAgentStore } from '@/store/AgentStore';
import { useSettingsStore } from '@/store/SettingsStore';
import ArrowRightIcon from '@/assets/icons/Arrow_Right_SM.svg';

import ActionMessage from './chunks/ActionMessage.vue';
import AnswerMessage from './chunks/AnswerMessage.vue';
import ButtonsMessage from './chunks/ButtonsMessage.vue';
import UserMessage from './chunks/UserMessage.vue';
import FilesMessage from './chunks/FilesMessage.vue';
import ToolResultMessage from './chunks/ToolResultMessage.vue';
import LoadingIndicator from './chunks/LoadingIndicator.vue';
import ErrorNotification from './chunks/ErrorNotification.vue';
import EmptyChat from './chunks/EmptyChat.vue';

import { useActionStatus } from './composables/useActionStatus';
import { useAutoScroll } from './composables/useAutoScroll';
import { useTextarea } from './composables/useTextarea';

const props = defineProps({
    prefillMessage: { type: String, default: '' },
    prefillMessageId: { type: String, default: '' },
    projectId: { type: String, default: null },
    sender: { type: Object, default: null },
    existingSessionId: { type: String, default: null },
    inline: { type: Boolean, default: false },
});

const emit = defineEmits(['session-created']);

const agentStore = useAgentStore();
const { messages, isLoading, isStreaming, error, sessionId, sessions, isConfirmation, hasMoreMessages } = storeToRefs(agentStore);

const settingsStore = useSettingsStore();

const messageText = ref('');
const chatContentRef = ref(null);
const selectedButtons = ref(new Map());

// --- Composables ---

const { getActionStatus } = useActionStatus(messages);
useAutoScroll(chatContentRef, [() => messages.value.length, isStreaming]);
const { textareaRef, adjustHeight, handleKeydown } = useTextarea(sendMessage);

// --- Infinite scroll (вверх — старые сообщения) ---

const topSentinelRef = ref(null);
let observer = null;

const isSentinelVisible = () => {
    const sentinel = topSentinelRef.value;
    const container = chatContentRef.value;
    if (!sentinel || !container) return false;
    const sr = sentinel.getBoundingClientRect();
    const cr = container.getBoundingClientRect();
    return sr.bottom >= cr.top && sr.top <= cr.bottom;
};

const loadMoreIfSentinelVisible = async () => {
    while (hasMoreMessages.value && !isLoading.value && isSentinelVisible()) {
        const el = chatContentRef.value;
        const oldScrollHeight = el?.scrollHeight ?? 0;
        await agentStore.loadMoreMessages(sessionId.value);
        await nextTick();
        if (el && el.scrollHeight !== oldScrollHeight) {
            el.scrollTop = el.scrollHeight - oldScrollHeight;
        }
    }
};

const setupObserver = () => {
    if (observer) observer.disconnect();

    observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !isLoading.value) {
            loadMoreIfSentinelVisible();
        }
    }, {
        root: chatContentRef.value,
        threshold: 0.1
    });

    if (topSentinelRef.value) {
        observer.observe(topSentinelRef.value);
    }
};

// --- Message type predicates ---

const isActionMessage = (msg) =>
    msg.role === 'assistant' &&
    (msg.typeChunk === 'CONFIRMATION_REQUIRED' || msg.typeChunk === 'AUTO_ACTION' || msg.content?.action);

const isAnswerMessage = (msg) =>
    msg.role === 'assistant' && msg.content?.type === 'answer';

const isButtonsMessage = (msg) =>
    msg.role === 'assistant' && msg.typeChunk === 'BUTTONS';

const isFilesMessage = (msg) => msg.typeChunk === 'FILES_RESULT';

const isToolMessage = (msg) => msg.typeChunk === 'TOOL';

const isHiddenToolMessage = (msg) =>
    !settingsStore.showToolsResult &&
    (isFilesMessage(msg) || isToolMessage(msg));

const isUserMessage = (msg) =>
    msg.role === 'user' &&
    !msg.isButtonChoice &&
    !msg.isToolResult &&
    !String(msg.content).startsWith('[HIDDEN]');

// --- Button selection ---

const isButtonSelected = (index) => {
    if (selectedButtons.value.has(index)) return true;
    const nextMsg = messages.value[index + 1];
    return nextMsg && nextMsg.role === 'user';
};

const getSelectedLabel = (index) => {
    if (selectedButtons.value.has(index)) {
        return selectedButtons.value.get(index);
    }
    const nextMsg = messages.value[index + 1];
    if (nextMsg?.role === 'user' && nextMsg.isButtonChoice) {
        return nextMsg.content;
    }
    return null;
};

// --- Actions ---

const handleButtonClick = async (actionId, value, label, index) => {
    selectedButtons.value.set(index, label);
    await agentStore.sendButtonChoice(actionId, value, label);
};

const confirmAction = async (pendingId) => {
    await agentStore.confirmAction(pendingId);
};

const rejectAction = async (pendingId) => {
    await agentStore.rejectAction(pendingId);
};

async function sendMessage() {
    if (!messageText.value.trim() || isStreaming.value) return;
    const text = messageText.value;
    messageText.value = '';
    await agentStore.sendMessage(text);
    adjustHeight();
}

async function quickSend(text) {
    if (isStreaming.value) return;
    await agentStore.sendMessage(text);
}

// --- Session ---

const activeSession = computed(() =>
    sessions.value.content?.find(s => s.id === sessionId.value)
);

watch(sessionId, async () => {
    selectedButtons.value.clear();
    await nextTick();
    setupObserver();
});

watch(isLoading, async (loading) => {
    if (!loading) {
        await nextTick();
        loadMoreIfSentinelVisible();
    }
});

onMounted(async () => {
    adjustHeight();
    setupObserver();
    if (props.existingSessionId) {
        await agentStore.selectSession(props.existingSessionId);
    } else if (props.prefillMessage) {
        if (!props.prefillMessageId) {
            return;
        }
        await agentStore.createSession(props.prefillMessageId);
        emit('session-created', { messageId: props.prefillMessageId, sessionId: agentStore.sessionId });
        const projectLine = props.projectId ? `ID проекта: ${props.projectId}\n` : '';
        const senderLine = props.sender
            ? `Отправитель: ${props.sender.fullName || ''} (ID пользователя (userId): ${props.sender.userId || 'Null'} ID сотрудника (companyMemberId): ${props.sender.companyMemberId || 'Null'}) \n`
            : '';
        const fullMessage = `[HIDDEN]Сообщение от сотрудника. Твоя задача:\n1. Определи что сотрудник сделал или сообщает.\n2. Немедленно найди соответствующие этапы и задачи в проекте — не жди уточнений от пользователя.\n3. Выполни нужные действия (обнови статус, запиши данные и т.д.).\nНЕ задавай уточняющих вопросов — действуй самостоятельно на основе найденных данных.\n${projectLine}${senderLine}Вот сообщение:\n${props.prefillMessage}`;
        await agentStore.sendMessage(fullMessage);
    }
});

onUnmounted(() => {
    observer?.disconnect();
});
</script>

<style lang="scss" scoped>
.chat-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;

    &--inline {
        .chat-title {
            display: none;
        }
    }
}

.chat-scroll {
    flex: 1;
    overflow-y: auto;
}

.chat-title {
    margin-bottom: 14px;
    text-align: center;
    flex-shrink: 0;

    @media (max-width: 768px) {
        display: none;
    }
}

.chat-container {
    max-width: 800px;
    height: 400px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
}

.chat-content {
    margin: 0 8px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.top-sentinel {
    height: 1px;
    flex-shrink: 0;
}

.chat-input-wrapper {
    box-sizing: border-box;
    flex-shrink: 0;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;

    @media (max-width: 768px) {
        padding: 10px 10px;
    }
}

.chat-input-area {
    display: flex;
    gap: 10px;
    background-color: var(--color-bg-dark);
    border-radius: 12px;
    border: 1px solid var(--color-border);
    overflow: hidden;

    .chat-input {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 4px;
        padding-bottom: 0;
        padding-top: 8px;
    }

    .input-button {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px;
        padding-right: 0;

        .button-image {
            border-radius: 10px;
        }
    }

    textarea {
        width: 100%;
        border: 0;
        outline: none;
        padding: 4px;
        resize: none;
        overflow-y: auto;
        min-height: 24px;
        max-height: 200px;
        background: transparent;
        color: var(--color-text);
        box-sizing: border-box;
        line-height: 1.2;

        &:focus {
            outline: none;
            box-shadow: none;
        }
    }
}
</style>
